// ChatWindow.tsx
import React, {useRef, useState, useCallback, useEffect} from 'react';
import {Box, Fade, IconButton, Stack, Tooltip} from '@mui/material';
import {useChatContext} from '../context';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import CallEndOutlinedIcon from '@mui/icons-material/CallEndOutlined';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import ExpandCircleDownOutlinedIcon from '@mui/icons-material/ExpandCircleDownOutlined';
import {ChatEnterField} from './ChatEnterField';
import {ChatMessages} from './ChatMessages';
import {GroupChatCreateModal} from './GroupChatCreateModal';
import {ContactOptionItem} from './ContactOptionItem';
import {useCallContext} from '../CallContext';
import {AudioBars} from './AudioBars/AudioBars';
import {useCallTimer} from '../utils';
import {useTranslation} from 'react-i18next';
import {ArrowBackIos} from '@mui/icons-material';
import {useDeviceInfo} from 'utils/hooks/useDeviceInfo';

export default function ChatWindow() {
    const {t} = useTranslation(['header']);
    const {selectedChat, setSelectedChat} = useChatContext();
    const {startCall, state, endCall, isMuted, toggleMute, callError, callStartedAt, activePeer} =
        useCallContext();

    const {isMobile} = useDeviceInfo();

    const scrollToBottomRef = useRef<() => void>(() => {});
    const [showScrollButton, setShowScrollButton] = useState(false);

    const handleScrollPositionChange = useCallback((isAtBottom: boolean) => {
        setShowScrollButton(!isAtBottom);
    }, []);

    useEffect(() => {
        setShowScrollButton(false);
    }, [selectedChat?.id]);

    const isCallingOrInCall = state === 'calling' || state === 'in-call';
    const isPrivate = selectedChat?.type === 'PRIVATE';
    const isGroupChat = selectedChat?.type === 'GROUP';
    const isPeerChat = isCallingOrInCall && isPrivate && selectedChat?.title === activePeer;

    const barColor = state === 'in-call' ? 'rgb(12, 202, 110)' : 'rgb(76, 76, 76)';
    const timer = useCallTimer(callStartedAt);

    // Кнопка недоступна:
    // - если чат не приватный
    // - если есть активный вызов, но он НЕ с этим собеседником
    const callDisabled = !isPrivate || (isCallingOrInCall && !isPeerChat);

    const tooltipTitle = !isPrivate
        ? t('chat.callsAvailableOnlyInPrivateChats')
        : isPeerChat
          ? t('chat.endCall')
          : isCallingOrInCall
            ? (t('chat.alreadyOnAnotherCall') ?? 'У вас уже есть активный звонок')
            : t('chat.startCall');

    return (
        <Stack
            direction='column'
            height='100%'
            justifyContent='space-between'
            overflow='hidden'
            gap={1}
        >
            {selectedChat && (
                <>
                    <Stack
                        flexShrink={0}
                        direction='row'
                        alignItems='center'
                        justifyContent='space-between'
                        sx={{
                            height: 56,
                            py: 1,
                            pl: 2,
                            borderBottom: '1px solid var(--mui-palette-divider)',
                        }}
                    >
                        <Stack
                            direction='row'
                            alignItems='center'
                            gap={1}
                            sx={{minWidth: 0}}
                        >
                            {isMobile && (
                                <ArrowBackIos
                                    onClick={() => {
                                        setSelectedChat(null);
                                    }}
                                />
                            )}
                            {isGroupChat ? (
                                <GroupChatCreateModal selectedChat={selectedChat} />
                            ) : (
                                <ContactOptionItem
                                    option={selectedChat}
                                    size={32}
                                    sx={{pl: 1}}
                                />
                            )}
                        </Stack>

                        <Stack
                            direction='row'
                            alignItems='center'
                            gap={1}
                        >
                            <Fade
                                in={isPeerChat}
                                unmountOnExit
                            >
                                <Box>
                                    <AudioBars barColor={barColor} />
                                </Box>
                            </Fade>

                            <Fade
                                in={isPeerChat && state === 'in-call'}
                                unmountOnExit
                            >
                                <Tooltip title={isMuted ? t('chat.unmute') : t('chat.mute')}>
                                    <IconButton
                                        onClick={toggleMute}
                                        color={isMuted ? 'error' : 'default'}
                                        size='small'
                                    >
                                        {isMuted ? <MicOffIcon /> : <MicIcon />}
                                    </IconButton>
                                </Tooltip>
                            </Fade>

                            <Fade
                                in={isPeerChat && !!callStartedAt}
                                unmountOnExit
                            >
                                <Box sx={{fontVariantNumeric: 'tabular-nums'}}>{timer}</Box>
                            </Fade>

                            {callError?.message === 'busy' && (
                                <Box sx={{ml: 1, fontSize: 12, opacity: 0.75}}>
                                    {t?.('chat.userBusy') ?? 'Занято'}
                                </Box>
                            )}

                            <Tooltip title={tooltipTitle}>
                                <Box
                                    component='span'
                                    sx={{display: 'inline-flex'}}
                                >
                                    <IconButton
                                        disabled={callDisabled}
                                        onClick={() => {
                                            if (callDisabled) return;
                                            if (isPeerChat) endCall();
                                            else if (isPrivate) startCall(selectedChat.title);
                                        }}
                                    >
                                        {isPeerChat ? (
                                            <CallEndOutlinedIcon color='error' />
                                        ) : (
                                            <LocalPhoneOutlinedIcon
                                                color={callDisabled ? 'disabled' : 'success'}
                                            />
                                        )}
                                    </IconButton>
                                </Box>
                            </Tooltip>
                        </Stack>
                    </Stack>

                    <Stack
                        sx={{
                            flexShrink: 2,
                            flexGrow: 2,
                            minHeight: 0,
                            overflow: 'hidden',
                            height: '100%',
                            position: 'relative',
                        }}
                    >
                        <ChatMessages
                            scrollToBottomRef={scrollToBottomRef}
                            onScrollPositionChange={handleScrollPositionChange}
                        />

                        <Fade
                            in={showScrollButton}
                            timeout={200}
                        >
                            <IconButton
                                aria-label='scroll to bottom'
                                onClick={() => scrollToBottomRef.current()}
                                color='primary'
                                size='medium'
                                sx={{
                                    borderRadius: '50%',
                                    position: 'absolute',
                                    bottom: 15,
                                    right: 15,
                                    zIndex: 1000,
                                    backgroundColor: 'var(--mui-palette-action-hover)',
                                    '&:hover': {
                                        backgroundColor: 'var(--mui-palette-background-default)',
                                    },
                                }}
                            >
                                <ExpandCircleDownOutlinedIcon fontSize='large' />
                            </IconButton>
                        </Fade>
                    </Stack>

                    <Box
                        sx={{
                            minHeight: 100,
                            flexGrow: 1,
                            borderTop: '1px solid var(--mui-palette-divider)',
                        }}
                    >
                        <ChatEnterField />
                    </Box>
                </>
            )}
        </Stack>
    );
}
